import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AlertModal from '../components/AlertModal';
import { orderAPI } from '../utils/api';

function OrderCompletePage() {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'info', message: '', onConfirm: null });
  const navigate = useNavigate();

  const showAlert = (type, message, onConfirm = null) => {
    setAlertModal({ isOpen: true, type, message, onConfirm });
  };

  const closeAlert = () => {
    const onConfirm = alertModal.onConfirm;
    setAlertModal({ isOpen: false, type: 'info', message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  useEffect(() => {
    const orderUuid = searchParams.get('order');
    if (!orderUuid) {
      showAlert('error', '주문 정보가 없습니다.', () => navigate('/'));
      return;
    }
    loadOrderDetails(orderUuid);
  }, [searchParams]);

  const loadOrderDetails = async (orderUuid) => {
    try {
      const orders = await orderAPI.getOrders();
      const foundOrder = orders.find(o => o.uuid === orderUuid);

      if (foundOrder) {
        setOrder(foundOrder);
      } else {
        showAlert('error', '주문을 찾을 수 없습니다.', () => navigate('/'));
      }
    } catch (err) {
      console.error('주문 조회 실패:', err);
      showAlert('error', '주문 정보를 불러오지 못했습니다.', () => navigate('/'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-50 mb-4">
              <Loader2 className="w-6 h-6 text-teal-500 animate-spin" />
            </div>
            <p className="text-gray-500">주문 정보를 불러오는 중...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-teal-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">주문이 완료되었습니다!</h1>
              <p className="text-gray-500">주문해 주셔서 감사합니다.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
              <h2 className="font-semibold text-gray-900 mb-4">주문 정보</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">주문번호</span>
                  <span className="font-mono text-sm text-gray-900">{order.uuid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">주문 상태</span>
                  <span className="font-medium text-teal-600">
                    {order.status === 'PAID' ? '결제완료' :
                     order.status === 'PENDING' ? '결제대기' :
                     order.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">결제 금액</span>
                  <span className="font-bold text-lg text-teal-600">{order.amount?.toLocaleString() || 0}원</span>
                </div>
              </div>
            </div>

            {order.address && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                <h2 className="font-semibold text-gray-900 mb-4">배송 정보</h2>
                <div className="space-y-2 text-gray-600">
                  <p><span className="text-gray-500">받는 분:</span> {order.address.recipient}</p>
                  <p><span className="text-gray-500">연락처:</span> {order.address.phone}</p>
                  <p><span className="text-gray-500">주소:</span> [{order.address.postal_code}] {order.address.address} {order.address.detail_address}</p>
                  {order.address.request && (
                    <p><span className="text-gray-500">배송 메시지:</span> {order.address.request}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/orders')}
                className="flex-1 bg-teal-500 text-white py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors"
              >
                주문 내역 보기
              </button>
              <button
                onClick={() => navigate('/products')}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                쇼핑 계속하기
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        message={alertModal.message}
      />
    </div>
  );
}

export default OrderCompletePage;
